/**
 * ONE DRIVER'S MONEY, WEEKEND BY WEEKEND AND SEASON BY SEASON — D-EZ-82.
 *
 * Howard, 22 Aug 2026, looking at a driver card: *"on a driver's card, the
 * finances dont track per week? Shouldnt they? Can they?"*
 *
 * They already did. `foldMoney` has built `book.byRace[raceNumber]` for every
 * account since the ledger was written, and nothing on any screen read it. The
 * money was being kept per weekend and shown only as a season total.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 * ⚠ AND THE FIGURE LABELLED "CAREER WINNINGS" WAS NOT CAREER WINNINGS.
 *
 * `DriverDossier`'s `gather()` read `career.ledger` — the ledger of ONE settled
 * season — and never touched the earlier seasons in `history` or the season
 * currently being played. Measured on two real seasons, seed 424242:
 *
 *     season 1 winnings      768.1 Mb
 *     season 2 winnings      584.6 Mb
 *     true career          1,352.7 Mb
 *     what the card showed   584.6 Mb   ← 57% short
 *
 * It always read LOW and it always looked plausible, which is the worst
 * combination a number can have: a driver in his sixth season showed one
 * season's money under a label that said career, and nothing about the figure
 * invited a second look. The longer the career, the wronger it got.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * ⚠ AND THE SEASON BEING PLAYED COUNTS. A career is not "what has been settled"
 * — Howard's own league was mid-season-2 at race 7, so the card was reading a
 * settled season 1 and showing him nothing about the seven races he had just
 * run. Money won on Sunday is money won. `liveEvents` is that half.
 *
 * PURE ON PURPOSE. This is arithmetic about money, so it is a module with its
 * own tests rather than a fold inside a component — the same reasoning that put
 * `money.ts` in the domain rather than in a screen.
 */

import { CAREER_WINNINGS_CATEGORIES, type MoneyEvent } from '../domain/finance/money.ts';
import type { CareerSeason } from '../lib/careerRunner.ts';

const isWinnings = (e: MoneyEvent): boolean =>
  e.amountMb > 0 && (CAREER_WINNINGS_CATEGORIES as readonly string[]).includes(e.category);

/** One weekend of one driver's money. */
export interface RaceMoney {
  seasonNumber: number;
  raceNumber: number;
  /** The event's own name, when the season knows it. */
  raceName: string | null;
  /** Purse and contingency — what he WON on the racetrack. */
  wonMb: number;
  /** Everything that moved on his book that weekend, winnings included. */
  netMb: number;
  /** Career winnings through the end of this weekend. */
  runningMb: number;
}

export interface DriverMoney {
  /** Every season's winnings summed, the one being played included. */
  careerWinningsMb: number;
  /** How many seasons he has money in. Not how many exist. */
  seasonsCounted: number;
  /** Oldest first, across every season. */
  races: RaceMoney[];
  /** The biggest and smallest weekends, for a summary line. */
  best: RaceMoney | null;
  worst: RaceMoney | null;
}

/**
 * A driver's weekends out of one season's money events.
 *
 * ⚠ `raceNumber === null` IS NOT RACE ZERO. The off-season and pre-season book
 * real money — a signing bonus, a sponsorship, the entry fee for a year — and
 * `MoneyEvent.raceNumber` is null for all of it. Folding those into a weekend
 * would put a winter's sponsorship into whatever race sorted first. They are
 * counted in the career total and excluded from the race list, which is what
 * "per week" means.
 */
function weekendsOf(
  events: readonly MoneyEvent[],
  driverId: string,
  seasonNumber: number,
  nameOfRace: (n: number) => string | null,
): { races: Omit<RaceMoney, 'runningMb'>[]; total: number } {
  const won = new Map<number, number>();
  const net = new Map<number, number>();
  let total = 0;

  for (const e of events) {
    if (e.account.kind !== 'driver' || e.account.id !== driverId) continue;
    if (isWinnings(e)) total += e.amountMb;
    if (e.raceNumber === null) continue;
    net.set(e.raceNumber, (net.get(e.raceNumber) ?? 0) + e.amountMb);
    if (isWinnings(e)) won.set(e.raceNumber, (won.get(e.raceNumber) ?? 0) + e.amountMb);
  }

  const races = [...net.keys()]
    .sort((a, b) => a - b)
    .map((raceNumber) => ({
      seasonNumber,
      raceNumber,
      raceName: nameOfRace(raceNumber),
      wonMb: won.get(raceNumber) ?? 0,
      netMb: net.get(raceNumber) ?? 0,
    }));

  return { races, total };
}

/** The event names of a settled season, by race number. */
function namesOf(career: CareerSeason): (n: number) => string | null {
  const byNumber = new Map<number, string>();
  for (const r of career.season.races) byNumber.set(r.raceNumber, r.scheduled.raceName);
  return (n) => byNumber.get(n) ?? null;
}

export interface LiveMoney {
  seasonNumber: number;
  events: readonly MoneyEvent[];
  raceName: (n: number) => string | null;
}

/**
 * Everything about one driver's money, across everything he has raced.
 *
 * `settled` is oldest-first; `live` is the season in progress, or null between
 * seasons. A driver who appears in neither comes back as an honest zero with an
 * empty list rather than as null — the card has a money panel either way, and
 * "he has not won anything yet" is a true thing to show a rookie.
 */
export function driverMoney(
  driverId: string,
  settled: readonly CareerSeason[],
  live: LiveMoney | null,
): DriverMoney {
  const collected: Omit<RaceMoney, 'runningMb'>[] = [];
  let career = 0;
  const seasonsWithMoney = new Set<number>();

  for (const season of settled) {
    const { races, total } = weekendsOf(
      season.finance.events,
      driverId,
      season.seasonNumber,
      namesOf(season),
    );
    if (total !== 0 || races.length > 0) seasonsWithMoney.add(season.seasonNumber);
    career += total;
    collected.push(...races);
  }

  if (live) {
    const { races, total } = weekendsOf(live.events, driverId, live.seasonNumber, live.raceName);
    if (total !== 0 || races.length > 0) seasonsWithMoney.add(live.seasonNumber);
    career += total;
    collected.push(...races);
  }

  /*
    ⚠ SORTED BEFORE THE RUNNING TOTAL IS TAKEN, not after. `settled` is handed
    in oldest-first and the live season is appended, so the order is usually
    already right — but "usually" is how a running total silently starts
    counting backwards on the one league that loaded its seasons out of order.
  */
  collected.sort((a, b) => a.seasonNumber - b.seasonNumber || a.raceNumber - b.raceNumber);

  let running = 0;
  const races: RaceMoney[] = collected.map((r) => {
    running += r.wonMb;
    return { ...r, runningMb: running };
  });

  const ranked = [...races].sort((a, b) => b.wonMb - a.wonMb);
  return {
    careerWinningsMb: career,
    seasonsCounted: seasonsWithMoney.size,
    races,
    best: ranked[0] ?? null,
    worst: ranked.length > 1 ? ranked[ranked.length - 1] : null,
  };
}

/** Just the season being shown, for a card that is scoped to one year. */
export const racesInSeason = (money: DriverMoney, seasonNumber: number): RaceMoney[] =>
  money.races.filter((r) => r.seasonNumber === seasonNumber);
